import React, { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import Select, { SingleValue } from "react-select";
import {
  IReactSelect,
  ISelectElement,
} from "../../../model/interface/keyValuePair";
import { useForm, SubmitHandler } from "react-hook-form";
import Loader from "../loader/loader";
import {
  getBundle,
  postBundle,
  postBundleProduct,
} from "../../../api/bundle.service";
import MultiSelect from "./multiSelect";
import { getStateAll } from "../../../api/subject.service";
import { IState } from "../../../model/interface/domain";

const AddBundle: React.FC<any> = ({
  productId,
  productName,
  existingSellableProducts,
  title,
  buttonText,
  confirmAction,
}: {
  confirmAction: (isConfirm: boolean) => void;
  productId: number;
  productName;
  existingSellableProducts: Array<any>;
  title: string;
  buttonText: string;
}) => {
  type Inputs = {
    newBundle: string;
    existingProduct: string;
  };

  const { register, handleSubmit } = useForm<Inputs>();
  const [isBundleDisabled, setIsBundleDisabled] = useState<boolean>(true);
  const [isNameDisabled, setIsNameDisabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultBundleValue: ISelectElement = {
    label: "Select Bundle",
    value: "0",
  };
  const [selectedBundle, setSelectedBundle] =
    useState<ISelectElement>(defaultBundleValue);
  const [allBundles, setAllBundles] = useState<any[]>([]);
  const [isExistingSelected, setIsExistingSelected] = useState<boolean>(false);
  const [isAlaCarte, setIsAlaCarte] = useState<boolean>(false);
  const [state, setState] = useState<IState[]>([]);
  const [selectedStates, setSelectedStates] = useState<Array<any>>([]);
  const [selectedSalesForceLevel, setSelectedSalesForceLevel] =
    useState<IReactSelect>();
  const salesForceLevel = [
    { label: "Elementary", value: 1 },
    { label: "Middle", value: 2 },
    { label: "High", value: 3 },
  ];

  const getStateData = async () => {
    const response = getStateAll(999, 1);
    const data = (await response).data;
    let newStateData = [];
    if (data.data.length > 0) {
      newStateData = data.data.map((eachContentData) => {
        return {
          value: eachContentData.stateId || 0,
          label: `${eachContentData.name}`,
        };
      });
    }
    setState(newStateData);
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    if (isExistingSelected) {
      postBundleProduct(
        parseInt(selectedBundle.value),
        productId.toString()
      ).then(() => {
        postSuccess();
      });
    } else {
      const bundleData = {
        bundleId: 0,
        name: data.newBundle,
        productIds: "",
        userId: 1,
        salesforceId: selectedSalesForceLevel?.value,
        stateIds: selectedStates?.map((ds) => ds.value)?.toString(),
        isAlaCarte: isAlaCarte,
        isActive: true,
      };
      postBundle(bundleData).then((bundle) => {
        if (bundle.data) {
          postBundleProduct(parseInt(bundle.data), productId.toString()).then(
            () => {
              postSuccess();
            }
          );
        }
      });
    }
  };

  const postSuccess = async () => {
    setTimeout(() => {
      confirmAction(true);
    }, 1000);
    const module = await import("react-toastify");
    module.toast.success("Product added successfully");
  };

  function handleBundleChange(selectedItem: any) {
    setIsExistingSelected(true);
    setSelectedBundle(selectedItem);
  }

  const fetchBundleList = () => {
    setLoading(true);
    const bundle = {
      name: "",
      orderByColumn: "name",
      sortByDesc: "ASC",
      pageSize: 9999,
      pageNumber: 1,
      isActive: true,
      subjectId: 0,
      contentAreaId: 0,
      stateId: 0,
      gradeId: 0,
      gradeLevelId: "",
    };
    getBundle(bundle).then((bundles) => {
      const bundleData = bundles.data.data.map((bundle) => {
        return {
          value: bundle.bundleId,
          label: `${bundle.name}`,
        };
      });

      setAllBundles(bundleData);
      setLoading(false);
    });
  };

  const handleRadioButtonChange = (type: string) => {
    if (type === "existingBundle") {
      setIsBundleDisabled(false);
      setIsNameDisabled(true);
    } else {
      setIsNameDisabled(false);
      setIsBundleDisabled(true);
    }
  };

  useEffect(() => {
    fetchBundleList();
    getStateData();
  }, []);

  const handleSelectedSalesForceLevel = (
    selectedItem: SingleValue<IReactSelect> | null
  ) => {
    if (selectedItem) {
      setSelectedSalesForceLevel(selectedItem);
    }
  };
  return (
    <div>
      {loading && <Loader />}

      <Transition.Root show={true} as={Fragment}>
        <Dialog
          as="div"
          className="fixed z-10 inset-0"
          onClose={() => confirmAction(false)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </TransitionChild>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left  shadow-xl transform transition-all sm:my-8 sm:align-middle sm:p-6 w-1/4">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4"></div>
                  <div className="sm:flex sm:items-start">
                    <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                      <DialogTitle as="h3" className="text-sm text-gray-700">
                        Add sellable product {`${productName}`} to bundle
                      </DialogTitle>
                      <div className="mt-2">
                        <div className="divide-y divide-gray-300 border border-gray-300 rounded-lg ">
                          <div className=" bg-gray-100 px-4 flex py-4">
                            <div className="w-full space-y-3">
                              <input
                                type="radio"
                                checked={!isNameDisabled}
                                onChange={() =>
                                  handleRadioButtonChange("newBundle")
                                }
                                className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                              />
                              <div className="w-full">
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                  Name
                                  <input
                                    id="newBundle"
                                    disabled={isNameDisabled}
                                    type="text"
                                    defaultValue=""
                                    className={`${
                                      isNameDisabled ? "bg-gray-100" : ""
                                    } mt-1 block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm w-full`}
                                    {...register("newBundle")}
                                  />
                                </label>
                              </div>
                              <div className="w-full">
                                <label className="mb-1 block text-sm font-medium text-gray-700">
                                  Salesforce Level
                                  <Select
                                    id="salesForceLevel"
                                    isDisabled={isNameDisabled}
                                    className={`${
                                      isNameDisabled ? "bg-gray-100" : ""
                                    } mt-1 block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm w-full`}
                                    placeholder=""
                                    options={salesForceLevel}
                                    value={selectedSalesForceLevel}
                                    onChange={handleSelectedSalesForceLevel}
                                  />
                                </label>
                              </div>

                              <div className="w-full">
                                <label className="text-sm text-gray-700">
                                  State
                                  <MultiSelect
                                    id="stateId"
                                    options={state}
                                    selectedItems={selectedStates}
                                    setSelectedItems={setSelectedStates}
                                    isDisabled={isNameDisabled}
                                    className={`${
                                      isNameDisabled ? "disabled" : ""
                                    }`}
                                  />
                                </label>
                              </div>

                              <div className="w-full">
                                <input
                                  id="isAlaCarte"
                                  type="checkbox"
                                  className="focus:ring-primary-color mr-2 h-4 w-4 text-primary-color border-gray-300 rounded"
                                  checked={isAlaCarte}
                                  onChange={(e) =>
                                    setIsAlaCarte(e.target.checked)
                                  }
                                  disabled={isNameDisabled}
                                />
                                <span>A La Carte</span>
                              </div>
                            </div>
                          </div>
                          <div className="px-4 flex w-full">
                            <div className="flex items-center w-full">
                              <input
                                onChange={() =>
                                  handleRadioButtonChange("existingBundle")
                                }
                                type="radio"
                                checked={!isBundleDisabled}
                                className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                              />
                              <div className="w-full py-4">
                                <label className="ml-3 mb-1 block text-sm font-medium text-gray-700">
                                  Add to existing bundle
                                  <div className="text-sm mt-1">
                                    <Select
                                      isDisabled={isBundleDisabled}
                                      options={allBundles}
                                      value={selectedBundle}
                                      isSearchable={true}
                                      onChange={handleBundleChange}
                                      theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                          ...theme.colors,
                                          primary25: "#B3B3B3",
                                          primary: "#041E42",
                                        },
                                      })}
                                      maxMenuHeight={250}
                                    ></Select>
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-color text-base font-medium text-white hover:bg-primary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {buttonText}
                    </button>
                    <button
                      type="button"
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color sm:mt-0 sm:w-auto sm:text-sm"
                      onClick={() => confirmAction(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default AddBundle;
